import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label, Textarea } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Plus, KanbanSquare, Layers, Calculator, Radar } from "lucide-react";
import { toast } from "sonner";
import RegisterDetailSheet from "./RegisterDetailSheet";
import InvestmentCaseBuilder from "./InvestmentCaseBuilder";
import FundingMatches from "./FundingMatches";
import { CLASS_TONE } from "./FundabilityPanel";

export const STAGE_TONE = {
  concept: "text-muted-foreground border-border",
  business_case: "text-[#00B4E6] border-[#00B4E6]/40",
  funding_ready: "text-accent border-accent/50",
  submitted: "text-[#FFB800] border-[#FFB800]/40",
  approved: "text-[#00E65B] border-[#00E65B]/40",
  implementation: "text-[#00E65B] border-[#00E65B]/40",
  mrv: "text-purple-400 border-purple-400/40",
};

const MRV_TEXT = {
  tracking: "text-[#00E65B]", overdue: "text-red-400", awaiting: "text-[#FFB800]",
  planned: "text-[#00B4E6]", not_planned: "text-muted-foreground",
};
const RAG_TEXT = { green: "text-[#00E65B]", amber: "text-[#FFB800]", red: "text-red-400" };

function CreateDialog({ meta, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const blank = {
    initiative_name: "", initiative_type: "energy_efficiency", esg_problem: "", owner: "",
    priority: "medium", investment_required: "", climate_response: "not_applicable",
    linked_risk: "", linked_target_id: "", technology: "",
  };
  const [f, setF] = useState(blank);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.initiative_name) return toast.error("Name required");
    setBusy(true);
    try {
      const envT = (meta?.linkable_env_targets || []).find((t) => t.id === f.linked_target_id);
      const body = {
        ...f,
        investment_required: f.investment_required === "" ? null : Number(f.investment_required),
        linked_target_id: f.linked_target_id || null,
        linked_target_label: envT ? `${envT.name} (FY${envT.target_year})` : null,
        linked_risk: f.linked_risk || null,
      };
      const r = await api.post("/initiatives/create", body);
      setOpen(false); setF(blank); onCreated(r.data.id);
      toast.success("Investment added to register");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="primary" onClick={() => setOpen(true)} data-testid="sf-reg-create-btn"><Plus className="h-4 w-4" /> New investment</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="sf-reg-create-dialog">
          <DialogHeader><DialogTitle className="font-display">New sustainability investment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Investment name</Label><Input value={f.initiative_name} onChange={(e) => set("initiative_name", e.target.value)} data-testid="sf-reg-input-name" placeholder="Refrigeration upgrade — Central Kitchen" /></div>
            <div className="col-span-2"><Label>ESG problem / opportunity</Label><Textarea rows={2} value={f.esg_problem} onChange={(e) => set("esg_problem", e.target.value)} data-testid="sf-reg-input-problem" placeholder="Energy intensity above internal benchmark; refrigeration is 38% of electricity load" /></div>
            <div><Label>Type</Label>
              <Select value={f.initiative_type} onValueChange={(v) => set("initiative_type", v)}>
                <SelectTrigger data-testid="sf-reg-input-type"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.types || []).map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.priorities || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Owner</Label><Input value={f.owner} onChange={(e) => set("owner", e.target.value)} data-testid="sf-reg-input-owner" /></div>
            <div><Label>Estimated CAPEX (S$)</Label><Input type="number" value={f.investment_required} onChange={(e) => set("investment_required", e.target.value)} data-testid="sf-reg-input-capex" /></div>
            <div><Label>Technology</Label><Input value={f.technology} onChange={(e) => set("technology", e.target.value)} placeholder="Inverter refrigeration" /></div>
            <div><Label>Climate response</Label>
              <Select value={f.climate_response} onValueChange={(v) => set("climate_response", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mitigation">Mitigation</SelectItem>
                  <SelectItem value="adaptation">Adaptation</SelectItem>
                  <SelectItem value="not_applicable">Not climate-related</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label>Contributes to target</Label>
              <Select value={f.linked_target_id || "none"} onValueChange={(v) => set("linked_target_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="sf-reg-input-target"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {(meta?.linkable_env_targets || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (FY{t.target_year})</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label>Addresses climate risk</Label>
              <Select value={f.linked_risk || "none"} onValueChange={(v) => set("linked_risk", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="sf-reg-input-risk"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {(meta?.linkable_risks || []).map((t) => <SelectItem key={t.id} value={t.title}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <div className="flex justify-end mt-2"><Button variant="primary" onClick={submit} disabled={busy} data-testid="sf-reg-create-submit">{busy ? "Adding…" : "Add to register"}</Button></div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function InvestmentRegister({ onNavigate, onOpenFinancingCase, onOpenMrv }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [builderId, setBuilderId] = useState(null);
  const [matchesId, setMatchesId] = useState(null);

  const load = async () => {
    const [r, m] = await Promise.all([api.get("/susfin/register"), api.get("/initiatives/meta")]);
    setData(r.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, []);
  if (builderId) return <InvestmentCaseBuilder id={builderId} onBack={() => { setBuilderId(null); load(); }} />;
  if (matchesId) return <FundingMatches id={matchesId} onBack={() => { setMatchesId(null); load(); }} onNavigate={onNavigate} onOpenFinancingCase={onOpenFinancingCase} />;
  if (!data) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading register…</div>;

  const items = (data.items || []).filter((i) => stageFilter === "all" || i.pipeline_stage === stageFilter);
  const stageLabel = (s) => (meta?.stage_labels || {})[s] || s;
  const potFunding = (i) => (Number(i.potential_grant_sgd || 0) + Number(i.potential_financing_sgd || 0)) || Number(i.funding_required || 0);

  return (
    <div className="space-y-4" data-testid="sf-register">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1 min-w-[280px]">
          {[["count", "Investments", (v) => v], ["capex", "Total CAPEX", formatSgd],
            ["potential_funding", "Potential funding", formatSgd], ["annual_savings", "Annual savings", formatSgd]].map(([k, l, fmt]) => (
            <div key={k} className="card-flat p-3" data-testid={`sf-reg-total-${k}`}>
              <div className="label-eyebrow text-[10px] mb-1">{l}</div>
              <div className="font-mono text-lg font-bold">{fmt(data.totals[k] || 0)}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/initiatives" className="text-xs text-muted-foreground hover:text-accent inline-flex items-center gap-1" data-testid="sf-reg-open-delivery">
            <KanbanSquare className="h-3.5 w-3.5" /> Delivery workspace
          </Link>
          {meta && <CreateDialog meta={meta} onCreated={(id) => { load(); setActive(id); }} />}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="sf-reg-stage-filter">
        <Button size="sm" variant={stageFilter === "all" ? "primary" : "outline"} onClick={() => setStageFilter("all")} data-testid="sf-reg-filter-all">All</Button>
        {(data.stages || []).map((s) => (
          <Button key={s.value} size="sm" variant={stageFilter === s.value ? "primary" : "outline"} onClick={() => setStageFilter(s.value)} data-testid={`sf-reg-filter-${s.value}`}>
            {s.label} <span className="font-mono ml-1 opacity-70">{s.count}</span>
          </Button>
        ))}
      </div>

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Investment</TableHead><TableHead>Stage</TableHead><TableHead>Fundability</TableHead><TableHead>MRV</TableHead><TableHead>Owner</TableHead>
            <TableHead className="text-right">CAPEX</TableHead><TableHead className="text-right">Potential funding</TableHead>
            <TableHead className="text-right">Savings / yr</TableHead><TableHead className="text-right">tCO₂e / yr</TableHead>
            <TableHead>Target</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} className="cursor-pointer hover:bg-surface-hover" onClick={() => setActive(i.id)} data-testid={`sf-reg-row-${i.id}`}>
                <TableCell className="text-sm font-medium max-w-[260px]">
                  <div className="truncate">{i.initiative_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{(i.initiative_type || "").replace(/_/g, " ")}</div>
                  {(i.funding_route?.grants || i.funding_route?.bank) && (
                    <div className="flex gap-1 mt-1 flex-wrap" data-testid={`sf-reg-route-${i.id}`}>
                      {i.funding_route.grants && (
                        <Badge variant="outline" className="text-[8px] uppercase text-[#00E65B] border-[#00E65B]/40">
                          Grants{i.route_links?.grant ? ` · ${i.route_links.grant.code} ${i.route_links.grant.status_label}` : ""}
                        </Badge>
                      )}
                      {i.funding_route.bank && (
                        <Badge variant="outline" className="text-[8px] uppercase text-[#00B4E6] border-[#00B4E6]/40">
                          Bank{i.route_links?.bank ? ` · ${i.route_links.bank.status_label}` : ""}
                        </Badge>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell><Badge variant="outline" className={`text-[9px] uppercase ${STAGE_TONE[i.pipeline_stage] || ""}`} data-testid={`sf-reg-stage-${i.id}`}>{stageLabel(i.pipeline_stage)}</Badge></TableCell>
                <TableCell>
                  {i.fundability && (
                    <div data-testid={`sf-reg-fund-${i.id}`}>
                      <span className={`font-mono text-sm font-bold ${(CLASS_TONE[i.fundability.classification.value] || "").split(" ")[0]}`}>{i.fundability.score}</span>
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground" data-testid={`sf-reg-fund-stage-${i.id}`}>{stageLabel(i.pipeline_stage)}</div>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {i.mrv_status && i.mrv_status.status !== "not_planned" ? (
                    <div data-testid={`sf-reg-mrv-${i.id}`}>
                      {i.mrv_status.achievement_pct != null
                        ? <span className={`font-mono text-sm font-bold ${RAG_TEXT[i.mrv_status.rag] || ""}`}>{i.mrv_status.achievement_pct}%</span>
                        : <span className={`text-[10px] uppercase tracking-wide ${MRV_TEXT[i.mrv_status.status]}`}>{i.mrv_status.status === "awaiting" ? "Awaiting" : i.mrv_status.status}</span>}
                      <div className="text-[9px] text-muted-foreground">{i.mrv_status.readings ? `${i.mrv_status.readings} reading${i.mrv_status.readings !== 1 ? "s" : ""}` : i.mrv_status.label}</div>
                    </div>
                  ) : <span className="text-xs text-muted-foreground/50">—</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{i.owner}</TableCell>
                <TableCell className="text-right font-mono text-xs">{i.capex_sgd ? formatSgd(i.capex_sgd) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs">{potFunding(i) ? formatSgd(potFunding(i)) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs">{i.expected_annual_savings || i.impact?.cost_savings_sgd ? formatSgd(i.expected_annual_savings || i.impact.cost_savings_sgd) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs">{i.expected_carbon_reduction || i.impact?.emissions_t ? formatNum(i.expected_carbon_reduction || i.impact.emissions_t) : "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate">{i.linked_target_label || i.target_kpi || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <button type="button" title="Open Investment Case Builder"
                      onClick={(e) => { e.stopPropagation(); setBuilderId(i.id); }}
                      className="text-muted-foreground hover:text-accent transition-colors p-1"
                      data-testid={`sf-reg-case-btn-${i.id}`}>
                      <Calculator className="h-4 w-4" />
                    </button>
                    <button type="button" title="Funding matches"
                      onClick={(e) => { e.stopPropagation(); setMatchesId(i.id); }}
                      className="text-muted-foreground hover:text-accent transition-colors p-1"
                      data-testid={`sf-reg-match-btn-${i.id}`}>
                      <Radar className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items.length === 0 && (
          <div className="p-10 text-center text-muted-foreground text-sm" data-testid="sf-reg-empty">
            <Layers className="h-7 w-7 mx-auto mb-2 opacity-50" />
            No investments {stageFilter !== "all" ? `at ${stageLabel(stageFilter)} stage` : "yet — add your first sustainability investment"}.
          </div>
        )}
      </div>

      <RegisterDetailSheet id={active} open={!!active} onClose={() => setActive(null)} meta={meta} onChanged={load}
        onOpenCaseBuilder={(iid) => { setActive(null); setBuilderId(iid); }}
        onOpenFundingMatches={(iid) => { setActive(null); setMatchesId(iid); }}
        onNavigate={onNavigate} onOpenFinancingCase={onOpenFinancingCase}
        onOpenMrv={onOpenMrv ? (iid) => { setActive(null); onOpenMrv(iid); } : undefined} />
    </div>
  );
}
